import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  CalendarClock,
  Boxes,
  Leaf,
  Recycle,
  TreePine,
  CheckCircle2,
  MapPin,
  Tag,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city, products = [] }) => {
  const t3 = products.find((p) => p.slug === "pack-cartons-demenagement-t3");
  const otherKits = [
    {
      slug: "pack-cartons-demenagement-t1",
      label: "Kit T1",
      sub: "Studio 20–30 m²",
    },
    {
      slug: "pack-cartons-demenagement-t2",
      label: "Kit T2",
      sub: "Appartement 35–50 m²",
    },
    {
      slug: "pack-cartons-demenagement-maison",
      label: "Kit Maison",
      sub: "À partir de 90 m²",
    },
  ];

  return (
    <>
      {/* ======= SECTION 2 — Livraison à Châtillon : 5 engagements en checklist ======= */}
      <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
        <div className="container-cdm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="overline" style={{ color: "#237cb5" }}>
                Livraison à domicile
              </div>
              <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
                Vos cartons livrés sur le pas de la porte à {city.name}
              </h2>
              <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
                Recevez l&apos;ensemble de vos fournitures de déménagement
                directement à votre adresse à {city.name}, sans contrainte
                de transport ni déplacement en magasin. Nous assurons une
                livraison rapide pour vous permettre de commencer vos
                préparatifs dans les meilleures conditions.
              </p>
              <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
                Profitez d&apos;une solution pratique pour obtenir
                rapidement tout le matériel nécessaire à votre déménagement.
              </p>
              <div className="mt-7">
                <Link
                  to="/cartons-demenagement"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
                  data-testid="city-chatillon-cta-shop"
                >
                  <Package className="h-4 w-4" strokeWidth={2.6} />
                  Configurer ma commande
                  <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-7 bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-7">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5] mb-3">
                Nos engagements
              </div>
              <ul className="space-y-3">
                {[
                  {
                    label:
                      "Livraison directement à votre domicile ou sur votre lieu de travail.",
                    Icon: Truck,
                  },
                  {
                    label:
                      "Fournitures soigneusement préparées et transportées.",
                    Icon: ShieldCheck,
                  },
                  {
                    label:
                      "Service rapide et fiable partout en Île-de-France.",
                    Icon: MapPin,
                  },
                  {
                    label:
                      "Horaires de livraison respectés pour plus de confort.",
                    Icon: CalendarClock,
                  },
                  {
                    label:
                      "Commande simple et réception de vos cartons en toute tranquillité.",
                    Icon: CheckCircle2,
                  },
                ].map(({ label, Icon }) => (
                  <li
                    key={label}
                    className="flex items-start gap-3 py-2 border-b border-[#EEF3F7] last:border-none"
                  >
                    <span className="flex items-center justify-center h-9 w-9 rounded-full bg-[#EEF3F7] text-[#237cb5] shrink-0">
                      <Icon className="h-[18px] w-[18px]" strokeWidth={2.4} />
                    </span>
                    <span className="text-[14px] text-[#0f1d2b] leading-snug mt-1.5">
                      {label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ======= SECTION 3 — Eco-responsable : cartons recyclables (thème vert) ======= */}
      <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6] relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute -top-32 -right-24 h-72 w-72 rounded-full bg-[#237cb5]/15 blur-3xl"
        />
        <div className="container-cdm relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#237cb5]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5] mb-4">
                <Leaf className="h-3.5 w-3.5" strokeWidth={2.6} />
                Démarche responsable
              </div>
              <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
                Des cartons recyclables pour un déménagement plus
                responsable
              </h2>
              <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
                Soucieux de réduire notre impact environnemental, nous
                proposons des cartons de déménagement fabriqués à partir de
                matériaux de qualité et 100&nbsp;% recyclables. Conçus pour
                être à la fois résistants et pratiques, ils offrent une
                excellente protection de vos biens tout au long du
                transport.
              </p>
              <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
                En choisissant nos fournitures, vous bénéficiez de cartons
                robustes adaptés à tous types de déménagements, tout en
                privilégiant une solution plus respectueuse de
                l&apos;environnement.
              </p>
            </div>

            <div className="lg:col-span-5 space-y-3">
              {[
                {
                  Icon: Recycle,
                  title: "100% recyclables",
                  body: "Fibres de cellulose certifiées, valorisables en boucle fermée.",
                },
                {
                  Icon: TreePine,
                  title: "Matières renouvelables",
                  body: "Issues de filières forestières gérées durablement.",
                },
                {
                  Icon: ShieldCheck,
                  title: "Résistance préservée",
                  body: "Aucun compromis sur la solidité face aux contraintes du transport.",
                },
              ].map(({ Icon, title, body }) => (
                <div
                  key={title}
                  className="flex items-start gap-3 bg-white rounded-2xl border border-[#cfdbe6] p-5"
                >
                  <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white shrink-0">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <div>
                    <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                      {title}
                    </h3>
                    <p className="mt-1 text-[13.5px] text-[#3a4856] leading-relaxed">
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ======= CTA Big Déménagement (au milieu) ======= */}
      <section className="py-12 bg-white">
        <div className="container-cdm">
          <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
        </div>
      </section>

      {/* ======= SECTION 4 — Packs tout-en-un : focus Kit T3 + teasers ======= */}
      <section className="py-14 md:py-20 bg-white">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFE9D6] border border-[#FF7A00]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-4">
              <Boxes className="h-3.5 w-3.5" strokeWidth={2.6} />
              Packs tout-en-un
            </div>
            <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              La méthode rapide pour cartonner
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Pourquoi perdre des heures à deviner le nombre de boîtes ou
              de rouleaux nécessaires&nbsp;? Nos packs prêts à
              l&apos;emploi résolvent l&apos;équation à votre place.
              Composés d&apos;un assortiment de cartons et d&apos;accessoires
              adaptés aux différentes tailles de logements, ils vous
              permettent d&apos;organiser votre déménagement plus
              facilement tout en maîtrisant votre budget. Une solution
              pratique, complète et disponible à la livraison partout dans
              les {city.deptName}.
            </p>
          </div>

          {/* T3 hero card */}
          <div className="mt-10 bg-gradient-to-br from-[#0f1d2b] to-[#1a2d3f] rounded-3xl overflow-hidden text-white">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
              {/* Left visual */}
              <div className="lg:col-span-5 bg-white/[0.04] p-8 md:p-10 border-b lg:border-b-0 lg:border-r border-white/10 flex flex-col justify-between min-h-[280px]">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF7A00] text-white text-[11px] uppercase tracking-[0.18em] font-bold">
                    <Tag className="h-3.5 w-3.5" strokeWidth={2.6} />
                    Coup de cœur
                  </div>
                  <h3 className="mt-5 font-display font-extrabold text-3xl md:text-4xl leading-tight text-white">
                    Kit T3
                  </h3>
                  <p className="mt-2 text-white/70 text-[15px]">
                    Appartement 3 pièces — 55 à 75 m²
                  </p>
                </div>

                <div className="mt-8 flex items-end justify-between">
                  <div>
                    {t3 && (
                      <div className="font-display font-extrabold text-3xl text-white">
                        {Number(t3.price).toFixed(2).replace(".", ",")} €
                      </div>
                    )}
                    <div className="text-white/60 text-[12.5px] mt-1">
                      Livré chez vous à {city.name}
                    </div>
                  </div>
                  <Link
                    to="/produit/pack-cartons-demenagement-t3"
                    className="inline-flex items-center gap-1.5 h-11 px-5 rounded-xl bg-[#FF7A00] hover:bg-[#e66f00] text-white font-bold text-[14px] transition-colors"
                    data-testid="city-chatillon-cta-t3"
                  >
                    Voir le kit T3
                    <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
                  </Link>
                </div>
              </div>

              {/* Right: 4 included items */}
              <div className="lg:col-span-7 p-8 md:p-10">
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-5">
                  Contenu du pack
                </div>
                <ul className="space-y-3">
                  {[
                    {
                      qty: "60",
                      title: "Cartons standards",
                      desc: "Le gros volume indispensable pour emballer le quotidien de toute la famille.",
                    },
                    {
                      qty: "30",
                      title: "Cartons à livres",
                      desc: "Costauds et parfaits pour vos collections de BD, romans, archives ou objets lourds.",
                    },
                    {
                      qty: "1",
                      title: "Grand rouleau de papier bulle (0,5 m × 100 m)",
                      desc: "100 mètres ! La sécurité ultime et ultragénéreuse pour toute votre vaisselle et vos bibelots.",
                    },
                    {
                      qty: "6",
                      title: "Rouleaux d'adhésif pro",
                      desc: "Le stock parfait pour scotcher à la chaîne comme un vrai pro de la manutention.",
                    },
                  ].map(({ qty, title, desc }) => (
                    <li
                      key={title}
                      className="flex items-start gap-4 py-3 border-b border-white/10 last:border-none"
                    >
                      <span className="flex items-center justify-center h-12 min-w-[3rem] px-2 rounded-lg bg-[#237cb5]/30 border border-[#237cb5]/40 text-white font-display font-extrabold text-lg shrink-0">
                        ×{qty}
                      </span>
                      <div>
                        <div className="font-display font-extrabold text-[15.5px] text-white leading-tight">
                          {title}
                        </div>
                        <div className="mt-1 text-[13.5px] text-white/70 leading-relaxed">
                          {desc}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Other kits teaser */}
          <div className="mt-10">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
              <h3 className="font-display font-extrabold text-xl text-[#0f1d2b]">
                Découvrez nos autres kits
              </h3>
              <Link
                to="/packs-cartons"
                className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#237cb5] hover:gap-2.5 transition-all"
                data-testid="city-chatillon-cta-all-kits"
              >
                Voir tous les kits
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {otherKits.map(({ slug, label, sub }) => {
                const product = products.find((p) => p.slug === slug);
                return (
                  <Link
                    key={slug}
                    to={`/produit/${slug}`}
                    className="group flex items-center justify-between bg-[#EEF3F7] hover:bg-white border border-[#cfdbe6] hover:border-[#237cb5] rounded-2xl p-5 transition-all"
                    data-testid={`city-chatillon-cta-${slug}`}
                  >
                    <div>
                      <div className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                        {label}
                      </div>
                      <div className="text-[13px] text-[#3a4856] mt-1">
                        {sub}
                      </div>
                      {product && (
                        <div className="mt-3 font-display font-extrabold text-[17px] text-[#237cb5]">
                          {Number(product.price).toFixed(2).replace(".", ",")} €
                        </div>
                      )}
                    </div>
                    <ArrowRight
                      className="h-5 w-5 text-[#237cb5] group-hover:translate-x-1 transition-transform"
                      strokeWidth={2.6}
                    />
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-chatillon-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
    </>
  );
};

export default { Sections };
